import Debug from 'debug';
import DfuTransportSerial from '../pc-nrf-dfu-js/src/DfuTransportSerial';
import { DfuError, ErrorCode } from '../pc-nrf-dfu-js/src/DfuError';
import * as slip from "../pc-nrf-dfu-js/src/util/slip";

const debug = Debug('dfu:usb');

// 'use strict';

// const util = require('util');
// const stream = require('stream');

const DEFAULT_FILTERS = [
    { vendorId: 0x2341, productId: 0x8036 }, // Arduino Leonardo
    { vendorId: 0x2341, productId: 0x8037 }, // Arduino Micro
    { vendorId: 0x239a, productId: 0x8011 }, // Adafruit Circuit Playground
];

function WebUSBSerialPort(options) {
    const self = this;

    options = options || {};
    self.filters = options.filters || DEFAULT_FILTERS;

    function handleDevice(device) {
        self.device = device;

        var readLoop = function () {
            self.device.transferIn(5, 64).then(result => {
                self.emit('data', new Buffer(result.data.buffer));
                readLoop();
            }, error => {
                self.emit('emit', error);
            });
        };

        self.device.open()
            .then(() => self.device.configuration)
            .then(config => {
                if (config.configurationValue == 1) {
                    return {};
                }
                throw new Error('Need to setConfiguration(1).');
            })
            .catch(error => self.device.setConfiguration(1))
            .then(() => self.device.claimInterface(2))
            .then(() => self.device.controlTransferOut({
                requestType: 'class',
                recipient: 'interface',
                request: 0x22,
                value: 0x01,
                index: 0x02,
            }))
            .then(() => {
                self.emit('open');
                readLoop();
            });
    }

    if (options.device) {
        handleDevice(options.device);
    } else {
        navigator.usb.requestDevice({ filters: self.filters })
            .then(handleDevice)
            .catch(err => {
                self.emit('error', err);
            });
    }
}

// util.inherits(WebUSBSerialPort, stream.Stream);

WebUSBSerialPort.prototype.open = function (callback) {
    this.emit('open');
    if (callback) {
        callback();
    }
};

WebUSBSerialPort.prototype.write = function (data, callback) {
    this.device.transferOut(4, data)
        .then(() => {
            if (callback) {
                callback(null);
            }
        })
        .catch(error => {
            if (callback) {
                callback(error);
            }
        });
};

WebUSBSerialPort.prototype.close = function (callback) {
    const self = this;
    self.device.controlTransferOut({
        requestType: 'class',
        recipient: 'interface',
        request: 0x22,
        value: 0x00,
        index: 0x02,
    })
        .then(() => {
            self.device.close();
            if (callback) {
                callback();
            }
        });
};

WebUSBSerialPort.prototype.flush = function (callback) {
    if (callback) {
        callback();
    }
};

WebUSBSerialPort.prototype.drain = function (callback) {
    if (callback) {
        callback();
    }
};

WebUSBSerialPort.DEFAULT_FILTERS = DEFAULT_FILTERS;

// module.exports = WebUSBSerialPort;

/**
 * USB serial DFU transport. Supports Nordic USB devices.
 *
 * Compared to DfuTransportSerial, this transport expects that the target
 * device disconnects/closes the serial port after a firmware image has
 * been sent. It is also instantiated with a serial number instead of a
 * serialport instance.
 */
export default class DfuTransportWebSerial extends DfuTransportSerial {
    // Creates the transport. A vid/pid is required,
    // and packetReceiptNotification may also be provided (defaults
    // to 16).
    constructor(webUSBPort, packetReceiveNotification = 16) {
        super(null, packetReceiveNotification);

        this.port = webUSBPort;
        this.portIsOpen = false;
    }

    // Given a Uint8Array, sends it as the main payload / "data object".
    // Returns a Promise that resolves when payload has been transferred
    // and target has disconnected.
    sendFirmwareImage(bytes) {
        return super.sendFirmwareImage(bytes)
            .then(() => this.waitForDisconnect());
    }

    async listenForSerial() {
        // if there's no serial port, return:
        if (!this.port) return;
        // while the port is open:
        while (this.port.readable) {
            // initialize the reader:
            this.reader = this.port.readable.getReader();
            try {
                // read incoming serial buffer:
                const { value, done } = await this.reader.read();
                if (value) {
                    // convert the input to a text string:
                    // TODO: make it possible to receive as binary:
                    this.incoming.data = new TextDecoder().decode(value);

                    // fire the event:
                    parent.dispatchEvent(this.dataEvent);
                }
                if (done) {
                    break;
                }
            } catch (error) {
                // if there's an error reading the port:
                console.log(error);
            } finally {
                this.reader.releaseLock();
            }
        }
    }

    async writeSerial(data) {
        const writer = this.port.writable.getWriter();
        await writer.write(data);
        await writer.close();
    }


    // Given a command (including opcode), perform SLIP encoding and send it
    // through the wire.
    // This ensures that the serial port is open by calling this.open() - the first
    // call to writeCommand will actually open the port.
    writeCommand(bytes) {
        let encoded = slip.encode(bytes);

        // Strip the heading 0xC0 character, as to avoid a bug in the nRF SDK implementation
        // of the SLIP encoding/decoding protocol
        encoded = encoded.subarray(1);

        // Cast the Uint8Array info a Buffer so it works on nodejs v6
        encoded = Buffer.from(encoded);

        return this.open().then(() => (
            new Promise(res => {
                const drained = this.writeSerial(encoded).then(() => {
                    debug(' send --> ', encoded);
                    if (drained) {
                        res();
                    } else {
                        this.port.once('drained', res);
                    }
                })
            })
        ));
    }

    async pollSerial() {
        // read incoming serial buffer:
        const { value, done } = await this.reader.read();
        if (value) {
            this.onRawData(value);
        }
        if (done) {
            console.log("pollSerial: done!");
            return;
        }
        setTimeout(this.pollSerial.bind(this), 10);
    }

    // Opens the port that matches the serial number provided to the transport.
    // Returns a Promise when opening is done.
    open() {
        if (!this.port) {
            return Promise.reject();
        }

        if (this.portIsOpen) {
            return Promise.resolve();
        }

        // console.log("Opening WebSerial device: " + this.port.vendorId + ":" + this.port.productId + " " + this.port.productName);
        console.log("Opening WebSerial device: " + this.port);


        // NOPE: return super.open();
        // Instead:
        return new Promise(async (res, rej) => {
            debug('Opening serial port.');

            try {
                await this.port.open({ baudRate: 115200 });
            } catch (e) {
                console.log("Couldn't open serial port: " + e);
                return rej(e);
            }
            this.portIsOpen = true;

            debug('Initializing SLIP decoder.');
            // Start listening for data, and pipe it all through a SLIP decoder.
            // This code will listen to events from the SLIP decoder instead
            // of from the serial port itself.
            this.slipDecoder = new slip.Decoder({
                onMessage: this.onData.bind(this),
            });

            // this.port.readable
            this.reader = await this.port.readable.getReader();
            setTimeout(this.pollSerial.bind(this), 10);

            return res();
        });
    }

    // Waits for the target to disconnect. Times out if not
    // disconnected after 5000 ms.
    waitForDisconnect() {
        if (!this.port || !this.portIsOpen) {
            debug('Port is already closed.');
            return Promise.resolve();
        }

        // Explicit close in all cases
        return new Promise(async resolve => {
            if (this.reader) {
                await this.reader.cancel();
            }
            await this.port.close();
            this.portIsOpen = false;
            // resolve() must be delayed so macOS doesn't immediately
            // open the same device again which occurs when it is plugged in a hub,
            // and win7-x64 might also have the chance to clean up.
            setTimeout(resolve, 2000);
        });
    }
}
